/**
 * Контракт SEM/CSR-редиректов: query исходного URL сохраняется,
 * params из target имеют приоритет при конфликте ключей.
 *
 * Особый случай: если у target есть хотя бы один ключ с префиксом `filter.`,
 * весь namespace `filter.*` из source отбрасывается перед мержем — иначе при
 * переименовании ключа фильтра (например, SEM-правило `filter.tip-tovara-nf-314`
 * → `filter.tip-tovara-314`) старый и новый ключи накапливались бы одновременно,
 * вызывая дублирование query и повторный редирект-цикл на следующей навигации.
 */

export const parseQueryParamsFromUrl: (url: string) => Record<string, string> = (
  url: string
): Record<string, string> => {
  const queryStartIndex: number = url.indexOf('?');
  if (queryStartIndex < 0) {
    return {};
  }

  const hashStartIndex: number = url.indexOf('#', queryStartIndex);
  const search: string = hashStartIndex >= 0 ? url.slice(queryStartIndex, hashStartIndex) : url.slice(queryStartIndex);

  return Object.fromEntries(new URLSearchParams(search));
};

const FILTER_PARAM_PREFIX: string = 'filter.';

export const mergeQueryParams: (
  sourceParams: Record<string, string>,
  targetParams: Record<string, string>
) => Record<string, string> = (
  sourceParams: Record<string, string>,
  targetParams: Record<string, string>
): Record<string, string> => {
  const targetHasFilterParams: boolean = Object.keys(targetParams).some((key: string) =>
    key.startsWith(FILTER_PARAM_PREFIX)
  );

  const effectiveSourceParams: Record<string, string> = targetHasFilterParams
    ? Object.fromEntries(
        Object.entries(sourceParams).filter(([key]: [string, string]) => !key.startsWith(FILTER_PARAM_PREFIX))
      )
    : sourceParams;

  return {
    ...effectiveSourceParams,
    ...targetParams,
  };
};

const getPathWithoutQueryAndHash: (url: string) => string = (url: string): string => {
  if (/^https?:\/\//i.test(url)) {
    const parsedUrl: URL = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  }

  const queryStartIndex: number = url.indexOf('?');
  const hashStartIndex: number = url.indexOf('#');
  let endIndex: number = url.length;

  if (queryStartIndex >= 0) {
    endIndex = Math.min(endIndex, queryStartIndex);
  }
  if (hashStartIndex >= 0) {
    endIndex = Math.min(endIndex, hashStartIndex);
  }

  return url.slice(0, endIndex);
};

const getHashFromUrl: (url: string) => string = (url: string): string => {
  const hashStartIndex: number = url.indexOf('#');
  return hashStartIndex >= 0 ? url.slice(hashStartIndex) : '';
};

export const mergeRedirectUrlWithOriginalQuery: (targetUrl: string, originalUrl: string) => string = (
  targetUrl: string,
  originalUrl: string
): string => {
  const mergedParams: Record<string, string> = mergeQueryParams(
    parseQueryParamsFromUrl(originalUrl),
    parseQueryParamsFromUrl(targetUrl)
  );
  const path: string = getPathWithoutQueryAndHash(targetUrl);
  const hash: string = getHashFromUrl(targetUrl);
  const queryString: string = new URLSearchParams(mergedParams).toString();

  if (!queryString) {
    return `${path}${hash}`;
  }

  return `${path}?${queryString}${hash}`;
};
